import {
    Directive,
    ElementRef,
    Input,
    Inject,
    Renderer,
    OnInit,
    Output,
    ViewContainerRef,
    AfterViewInit,
    EventEmitter
} from '@angular/core';

@Directive({
    selector: '[globe-container]',
    providers: [
        { provide: 'Topojson',  useValue: topojson },
        { provide: 'Crossfilter', useValue: crossfilter }
    ]
})
export class GlobeDirective implements OnInit {
    public globePaths = require('./world-110m.json');
    public countryNames = require('./world-110m-country-names.json')
    public countries;
    public countryPathsByName = {};
    public land;
    public heatmapArray = [];
    // default draw settings
    public backgroundColor = "black";
    public foregroundColor = "white";
    public center = [0, 0];
    public zoom = 3;
    public sens = 0.25;
    public windowWidth = 0;
    // public mode = 'all';
    public strokeColor = '#79CC2E';
    // canvas
    public canvas;
    public context;
    // d3
    public projection;
    public path;

    // The following will all change at runtime, but they are set to some value to
    // handle the case that the render function gets run before they are updated.
    public box = {
        width: 300,
        height: 300
    };
    public radius = this.box.height / 2 - 5;
    public scale = this.radius;
    public cosLat = 1;
    // set up variables for inactivity monitoring to activate spin
    public isInactive = true;
    public currentInactivity = 0;
    public inactivityTime = 5; // time in seconds of inactivity that the globe will start spinning
    // public inactivityZoom = 3; // max zoom level that the globe will spin if inactive
    public rotate = [0, 0]
    public rt = Date.now();
    public velocity = [.015, -0];
    public rainbowScale;

    // data storage and dimensions
    private crossfilterData;
    private timeDimension;
    private countryDimension;
    private countryGroup;

    // data streams
    @Input() private inboundStream;
    @Input() private outboundStream;
    @Input() private newStream;
    @Input() mode = "outbound";

    @Input() private parent;
    @Output() onGlobeDrag = new EventEmitter();
    @Output() modeChanged = new EventEmitter();
    constructor(
        private el: ElementRef,
        private renderer: Renderer,
        private viewContainer: ViewContainerRef,
        @Inject('Topojson') private topojson,
        @Inject('Document') private document: Document,
        @Inject('D3') private d3,
        @Inject('Window') private window,
        @Inject('Crossfilter') private crossfilter
    ) {}
    /////////////////////
    // LIFESTYLE HOOKS //
    /////////////////////
    ngOnInit() {
        // element setup
        let el = this.viewContainer.element;
        // this.canvas = this.renderer.createElement(el.nativeElement, 'canvas');
        this.canvas = this.el.nativeElement.querySelector('canvas');
        this.context = this.canvas.getContext("2d");
        this.projection = this.d3.geo.orthographic()
            .clipAngle(90);
        this.path = this.d3.geo.path()
            .projection(this.projection)
            .context(this.context);
        // heatmap linear scale
        this.rainbowScale = this.d3.scale.linear();

        // dragging
        this.d3.select(this.canvas)
            .call(this.d3.behavior.drag()
            .origin(() => {
                var r = this.projection.rotate();
                return {
                    x: r[0] / this.sens,
                    y: -r[1] / this.sens
                };
            })
            .on("drag", () => {
                var lng = -this.window.d3.event.x * this.sens;
                var lat = this.window.d3.event.y * this.sens;

                // Disallow rotation beyond the poles.
                lat = lat > 89 ? 89 : lat < -89 ? -89 : lat;

                this.center = [ lng, lat ];
                this.setCenter(this.center);
                // emit events for other visuals
                this.onGlobeDrag.emit({
                    value: this.center
                })
            }));
        this.resize(this.box);
        this.setWidth();

        // define countries, paths and lookups
        let countryById = {};
        this.countries = this.topojson.feature(this.globePaths, this.globePaths.objects.countries).features;
        for (let n = 0, len = this.countryNames.length; n < len; n++) {
            countryById[this.countryNames[n].id] = this.countryNames[n].name;
        }
        for (let c = 0, len = this.countries.length; c < len; c++) {
            this.countryPathsByName[countryById[this.countries[c].id]] = this.countries[c]
        }
        this.land = this.topojson.feature(this.globePaths, this.globePaths.objects.land);

        // crossfilter setup
        this.crossfilterData = new this.crossfilter();
        this.timeDimension = this.crossfilterData.dimension(function(d){ return d.time })
        this.countryDimension = this.crossfilterData.dimension(function(d){
            switch (d.vector) {
                case 'inbound':
                    return d.orig_country;
                case 'outbound':
                    return d.resp_country;
            }
        })
        this.countryGroup = this.countryDimension.group();

        // setup globe mode
        this.setGlobeMode(); // this will default to 'all'

        //sub to streams
        this.setupStreams();
        // listen for re-sub events when the site is changed
        this.newStream
            .asObservable()
            .subscribe(() => {
                this.setupStreams();
            })
    }
    ////////////
    // EVENTS //
    ////////////
    setupStreams() {
        // DATA SUBSCRIPTIONS
        this.outboundStream.subscribe((conn) => {
            // if (visibility.isHidden) { return }
            if (this.mode === 'inbound') { return } // do nothing if the globe's mode is set to inbound
            this.crossfilterData.add([conn]);
        })
        this.inboundStream.subscribe((conn) => {
            // if (visibility.isHidden) { return }
            if (this.mode === 'outbound') { return } // do nothing if the globe's mode is set to outbound
            this.crossfilterData.add([conn]);
        })
    }
    onMapDrag(event) {
        this.setCenter(event.center);
        this.zoom = event.zoom;
        // reset globe inactivity status
        this.resetInactivity();
        // because this fires also when the window is resized
        // lets also update the width so the zoom lines are proportional to the view
        this.setWidth();
    }
    /////////////
    // METHODS //
    /////////////
    checkInactive() {
        if (this.isInactive) { return }
        // if (map.zoom > this.inactivityZoom) { return }
        if (this.currentInactivity === this.inactivityTime) {
            this.rt = Date.now();
            this.isInactive = true;
            return
        }
        this.currentInactivity++;
        return;
    }
    resetInactivity() {
        this.currentInactivity = 0;
        this.isInactive = false;
    }
    resize(box) {
        this.canvas.width = box.width;
        this.canvas.height = box.height;
        this.projection.translate([box.width / 2, box.height / 2]);
        this.radius = box.height / 2 - 5;
    }
    setCenter(center) {
        const lat = center[1];
        this.cosLat = Math.cos(this.toRadians(lat));
        this.rotate = [ -center[0], -center[1] ];
        this.rotateMap(this.rotate)
    }
    rotateMap(rotate) {
        this.projection.rotate(rotate);
    }
    toRadians(deg){
        return deg / 180 * Math.PI;
    }
    getParentWidth() {
        if (!this.parent) { console.log('Parent attribute not defined in map'); return }
        let parent = this.document.getElementsByClassName(this.parent)[0];
        const width = parent.clientWidth;
        return width;
    }
    setWidth() {
        // this is so the globe is aware how wide the window is so it can set the view ring correctly
        this.windowWidth = this.getParentWidth();
    }
    public setGlobeMode(mode?){
        switch(mode) {
            case 'inbound':
                this.mode = mode;
                this.strokeColor = '#FF5D2A';
                this.rainbowScale.range(['#FF5D2A', '#662510']);
                this.timeDimension.filterAll();
                this.crossfilterData.remove(); // remove everything
                break
            case 'outbound':
                this.mode = mode;
                this.strokeColor = '#3459FF';
                this.rainbowScale.range(['#3459FF', '#101F62']);
                this.timeDimension.filterAll();
                this.crossfilterData.remove(); // remove everything
                break
            default:
                this.mode = 'all';
                this.strokeColor = '#79CC2E';
                this.rainbowScale.range(['#79CC2E', '#3D6718']);
        }
        this.modeChanged.emit({
            value: this.mode
        })
    }

    // DATA HANDLING
    filterCrossfilterByTime() {
        const now = Date.now();
        this.timeDimension.filter((d) => { return d < (now - 4000) });
        this.crossfilterData.remove(); // remove matches
        this.timeDimension.filterAll();
    }
    processData() {
        // filer out older items everting by time
        this.filterCrossfilterByTime();
        let max = 1, obj = {}; // set max number for rainow scale
        let all = this.countryGroup.all().map((d) => {
            // since we're already iterating over the array, lets set the max
            if (d.value > max) { max = d.value; }
            return { [d.key]: d.value }
        });
        if (all.length > 0) {
            obj = Object.assign.apply(Object, all);
        }
        this.rainbowScale.domain([1, max]);

        // HEATMAP INTERPOLATION
        let arr = [];
        // loop thorugh existing countries first
        for (let i = 0, len = this.heatmapArray.length; i < len; i++) {
            let o = this.heatmapArray[i];
            if (obj.hasOwnProperty(o.name)) {
                o.colors.start = o.colors.end; // swap colors
                o.colors.end = this.rainbowScale(obj[o.name]); // new color
                // delete value in object
                delete obj[o.name];
                arr.push(o);
            } else if (o.colors.end !== '#ffffff') {
                // don't push if the end is already white
                o.colors.start = o.colors.end;
                o.colors.end = '#ffffff';
                arr.push(o);
            }
        }
        // these are remaining values not previously
        // in the heatmap array, so we make them compatible
        for (let i in obj) {
            let o = {
                path: this.countryPathsByName[i],
                name: i,
                colors: {
                    start: '#ffffff',
                    end: '' // assigned below
                }
            }
            o.colors.end = this.rainbowScale(obj[o.name]) // new value
            arr.push(o);
        }
        // update the heatmap queue
        this.heatmapArray = arr;
    }
}


/** WEBPACK FOOTER **
 ** ./src/landing/directives/dashboard/globe.directive.ts
 **/